import { useEffect, useMemo } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';
import moment, { Moment } from 'moment';

import { ChallengeFormValues } from '../interfaces/ChallengeFormValues';
import {
  getDayAfterDate,
  getDayAfterToday,
  getDayBeforeDate,
  getFormattedDate,
} from '../../shared/utils/dateUtils';
import { Mode } from '../../general-types';
import { isObject } from '../../shared/utils/jsUtils';
import { dateFormat } from '../../shared/constants/apiDateFormat';

/**
 * Date constraint constants for challenge creation
 * All values represent number of days
 */
export const CHALLENGE_DATE_CONSTRAINTS = {
  /** Minimum days from today for Challenge Start Date (1 = tomorrow) */
  CHALLENGE_START_DATE_AHEAD: 1,
  /** Minimum duration of challenge (days between start and end) */
  MIN_CHALLENGE_DURATION_DAYS: 15,
  /** Days after Challenge Start Date for Registration End Date */
  REGISTRATION_DAYS_AFTER_START: 7,
  /** Days after Challenge Start Date for First Scan End Date */
  FIRST_SCAN_END_DAYS_AFTER_START: 7,
  /** Minimum days before Challenge End Date for Last Scan Start Date */
  LAST_SCAN_MIN_DURATION_DAYS: 7,
} as const;

/**
 * Calculates auto-calculated date value for form fields.
 * Returns null if date is invalid or has DatePicker error state (object with error).
 *
 * @param relatedDate - Source date string or null
 * @param processDate - Transform function (e.g., add/subtract days)
 * @returns Formatted date string or null (default value)
 */
export const getDateFormValue = (
  relatedDate: string | null,
  processDate: (date: string) => Moment,
): string | null => {
  return relatedDate && !isObject(relatedDate)
    ? getFormattedDate(processDate(relatedDate), dateFormat)
    : null;
};

type UseImportantDatesFormReturn = {
  challengeMinStartDate: Moment;
  challengeMaxStartDate: Moment | undefined;
  challengeMinEndDate: Moment;
  registrationMaxStartDate: Moment | undefined;
};

/**
 * useImportantDatesForm hook is used to get the validation and set auto-calculated values for the important dates section.
 *
 * @mode Auto-calculated fields behavior:
 * - EDIT mode: values are calculated from dependent fields and override form values
 * - VIEW mode: values are taken from form state (saved data from backend)
 * This ensures VIEW mode displays actual saved data, not recalculated values.
 *
 * @validation Challenge Dates Section
 * | Field                | Min                       | Max                | Type     |
 * |----------------------|---------------------------|--------------------|----------|
 * | Challenge Start Date | tomorrow (today + 1 day)   | Challenge End Date | editable |
 * | Challenge End Date   | Challenge Start + 15 days | —                  | editable |
 *
 * @validation Registration Dates Section
 * | Field                   | Min | Max                  | Type                           |
 * |-------------------------|-----|----------------------|--------------------------------|
 * | Registration Start Date | —   | Challenge Start Date | editable                       |
 * | Registration End Date   | —   | —                    | auto: Challenge Start + 7 days |
 *
 * @validation Measurement Dates Section
 * | Field            | Min                  | Max                    | Type                           |
 * |------------------|----------------------|------------------------|--------------------------------|
 * | First Scan Start | —                    | —                      | auto: Challenge Start Date     |
 * | First Scan End   | —                    | —                      | auto: Challenge Start + 7 days |
 * | Last Scan Start  | —                    | —                      | auto: Challenge End - 7 days   |
 * | Last Scan End    | —                    | —                      | auto: Challenge End Date       |
 *
 * @see https://lifebasellc.atlassian.net/wiki/spaces/DEV/pages/648544257/Challenge+create+and+edit+functionality#2.-Important-Dates
 */
export const useImportantDatesForm = (mode: Mode): UseImportantDatesFormReturn => {
  const { control, setValue } = useFormContext<ChallengeFormValues>();

  const challengeStartDate = useWatch({
    control,
    name: 'importantDates.startDate',
  });

  const challengeEndDate = useWatch({
    control,
    name: 'importantDates.endDate',
  });

  const challengeMinStartDate = useMemo(
    () => getDayAfterToday(CHALLENGE_DATE_CONSTRAINTS.CHALLENGE_START_DATE_AHEAD),
    [],
  );

  const challengeMaxStartDate = useMemo(
    () => (challengeEndDate ? moment(challengeEndDate) : undefined),
    [challengeEndDate],
  );

  const challengeMinEndDate = useMemo(
    () =>
      challengeStartDate
        ? getDayAfterDate(
            challengeStartDate,
            CHALLENGE_DATE_CONSTRAINTS.MIN_CHALLENGE_DURATION_DAYS,
          )
        : getDayAfterDate(
            challengeMinStartDate,
            CHALLENGE_DATE_CONSTRAINTS.MIN_CHALLENGE_DURATION_DAYS,
          ),
    [challengeStartDate, challengeMinStartDate],
  );

  // Registration Start Date: max = Challenge Start Date
  const registrationMaxStartDate = useMemo(
    () => (challengeStartDate ? moment(challengeStartDate) : undefined),
    [challengeStartDate],
  );

  // Registration End Date = Challenge Start + 7 days
  useEffect(() => {
    if (mode === 'EDIT') {
      const value = getDateFormValue(challengeStartDate, (date) =>
        getDayAfterDate(date, CHALLENGE_DATE_CONSTRAINTS.REGISTRATION_DAYS_AFTER_START),
      );

      setValue('importantDates.registrationEndDate', value);
    }
  }, [mode, challengeStartDate, setValue]);

  // First Scan Start = Challenge Start Date
  useEffect(() => {
    if (mode === 'EDIT') {
      setValue('importantDates.firstScanStartDate', challengeStartDate);
    }
  }, [mode, challengeStartDate, setValue]);

  // First Scan End = Challenge Start + 7 days
  useEffect(() => {
    if (mode === 'EDIT') {
      const value = getDateFormValue(challengeStartDate, (date) =>
        getDayAfterDate(date, CHALLENGE_DATE_CONSTRAINTS.FIRST_SCAN_END_DAYS_AFTER_START),
      );

      setValue('importantDates.firstScanEndDate', value);
    }
  }, [mode, challengeStartDate, setValue]);

  // Last Scan Start = Challenge End - 7 days
  useEffect(() => {
    if (mode === 'EDIT') {
      const value = getDateFormValue(challengeEndDate, (date) =>
        getDayBeforeDate(date, CHALLENGE_DATE_CONSTRAINTS.LAST_SCAN_MIN_DURATION_DAYS),
      );

      setValue('importantDates.lastScanStartDate', value);
    }
  }, [mode, challengeEndDate, setValue]);

  // Last Scan End = Challenge End Date
  useEffect(() => {
    if (mode === 'EDIT') {
      setValue('importantDates.lastScanEndDate', challengeEndDate);
    }
  }, [mode, challengeEndDate, setValue]);

  return {
    challengeMinStartDate,
    challengeMaxStartDate,
    challengeMinEndDate,
    registrationMaxStartDate,
  };
};
